// src/services/photoUploadInvoiceService.js
// Service for creating and managing photo upload extension/upgrade invoices
// Invoice formats: PUEX-YY-NNNNNNNNN (extension), PUUP-YY-NNNNNNNNN (upgrade)

import supabase from './supabase';

// Platform details (Invits / Forte Technologies)
export const PLATFORM_DETAILS = {
  name: 'Invits',
  tradeName: 'Forte Technologies',
  gstin: '22MIYPS5170D1ZN',
  pan: 'MIYPS5170D',
  address: 'Raipur, Chhattisgarh - 492001',
  state: 'Chhattisgarh',
  stateCode: '22',
  email: 'support@invits.co',
  hsnSac: '998314', // Online content services
};

// State codes for GST (first 2 digits of GSTIN)
export const STATE_CODES = {
  'Andhra Pradesh': '37',
  'Arunachal Pradesh': '12',
  'Assam': '18',
  'Bihar': '10',
  'Chhattisgarh': '22',
  'Goa': '30',
  'Gujarat': '24',
  'Haryana': '06',
  'Himachal Pradesh': '02',
  'Jharkhand': '20',
  'Karnataka': '29',
  'Kerala': '32',
  'Madhya Pradesh': '23',
  'Maharashtra': '27',
  'Manipur': '14',
  'Meghalaya': '17',
  'Mizoram': '15',
  'Nagaland': '13',
  'Odisha': '21',
  'Punjab': '03',
  'Rajasthan': '08',
  'Sikkim': '11',
  'Tamil Nadu': '33',
  'Telangana': '36',
  'Tripura': '16',
  'Uttar Pradesh': '09',
  'Uttarakhand': '05',
  'West Bengal': '19',
  'Andaman and Nicobar Islands': '35',
  'Chandigarh': '04',
  'Dadra and Nagar Haveli and Daman and Diu': '26',
  'Delhi': '07',
  'Jammu and Kashmir': '01',
  'Ladakh': '38',
  'Lakshadweep': '31',
  'Puducherry': '34',
};

/**
 * Get state code from state name
 */
export const getStateCode = (stateName) => {
  if (!stateName) return '';
  return STATE_CODES[stateName] || '';
};

/**
 * Get all Indian states for dropdown
 */
export const getIndianStates = () => {
  return Object.keys(STATE_CODES).sort();
};

/**
 * Get current financial year from server
 * Returns 2-digit year (e.g., "25" for FY 2025-26)
 */
export const getFinancialYear = () => {
  // Local calculation
  const now = new Date();
  const month = now.getMonth(); // 0-11
  const year = now.getFullYear();
  // Financial year: April to March
  // If month is Jan(0), Feb(1), Mar(2), FY started previous year
  const fyYear = month < 3 ? year - 1 : year;
  return fyYear.toString().slice(-2);
};

/**
 * Calculate GST breakdown based on customer state
 * Platform state: Chhattisgarh
 * Same state = CGST + SGST (intrastate)
 * Different state = IGST (interstate)
 */
export const calculatePhotoUploadGST = (baseAmount, customerState) => {
  const gstRate = 18;
  const gstAmount = parseFloat(((baseAmount * gstRate) / 100).toFixed(2));
  const platformState = PLATFORM_DETAILS.state;

  const isSameState = customerState &&
    customerState.toLowerCase().trim() === platformState.toLowerCase().trim();

  if (isSameState) {
    const halfGst = parseFloat((gstAmount / 2).toFixed(2));
    return {
      type: 'intrastate',
      cgst: halfGst,
      sgst: halfGst,
      igst: 0,
      totalGst: gstAmount,
      rate: gstRate,
    };
  } else {
    return {
      type: 'interstate',
      cgst: 0,
      sgst: 0,
      igst: gstAmount,
      totalGst: gstAmount,
      rate: gstRate,
    };
  }
};

/**
 * Get photo upload extension/upgrade invoice by ID
 */
export const getPhotoUploadInvoiceById = async (invoiceId) => {
  try {
    const { data, error } = await supabase
      .from('photo_upload_extension_invoices')
      .select('*')
      .eq('id', invoiceId)
      .single();

    if (error) throw error;

    return {
      success: true,
      invoice: data,
    };
  } catch (error) {
    console.error('Error fetching photo upload invoice:', error);
    return {
      success: false,
      error: error.message,
      invoice: null,
    };
  }
};

/**
 * Get photo upload extension/upgrade invoice by invoice number
 */
export const getPhotoUploadInvoiceByNumber = async (invoiceNumber) => {
  try {
    const { data, error } = await supabase
      .from('photo_upload_extension_invoices')
      .select('*')
      .eq('invoice_number', invoiceNumber)
      .single();

    if (error) throw error;

    return {
      success: true,
      invoice: data,
    };
  } catch (error) {
    console.error('Error fetching photo upload invoice:', error);
    return {
      success: false,
      error: error.message,
      invoice: null,
    };
  }
};

/**
 * Get all photo upload extension/upgrade invoices for the current user
 * @param {Object} options - Filter options
 */
export const getUserPhotoUploadInvoices = async (options = {}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    let query = supabase
      .from('photo_upload_extension_invoices')
      .select('*')
      .eq('user_id', user.id);

    // Apply filters
    if (options.invoiceType) {
      query = query.eq('invoice_type', options.invoiceType);
    }

    if (options.financialYear) {
      query = query.eq('financial_year', options.financialYear);
    }

    if (options.eventId) {
      query = query.eq('event_id', options.eventId);
    }

    // Order by generated date (newest first)
    query = query.order('generated_at', { ascending: false });

    // Limit if specified
    if (options.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) throw error;

    return {
      success: true,
      invoices: data || [],
      count: data?.length || 0,
    };
  } catch (error) {
    console.error('Error fetching user photo upload invoices:', error);
    return {
      success: false,
      error: error.message,
      invoices: [],
      count: 0,
    };
  }
};

/**
 * Get invoice statistics for current user
 */
export const getPhotoUploadInvoiceStats = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: invoices, error } = await supabase
      .from('photo_upload_extension_invoices')
      .select('total_amount, total_gst, invoice_type')
      .eq('user_id', user.id);

    if (error) throw error;

    const stats = {
      totalInvoices: invoices.length,
      totalSpent: invoices.reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0),
      totalGst: invoices.reduce((sum, inv) => sum + parseFloat(inv.total_gst || 0), 0),
      extensionCount: invoices.filter(inv => inv.invoice_type === 'PUEX').length,
      upgradeCount: invoices.filter(inv => inv.invoice_type === 'PUUP').length,
    };

    return {
      success: true,
      stats,
    };
  } catch (error) {
    console.error('Error fetching invoice stats:', error);
    return {
      success: false,
      error: error.message,
      stats: null,
    };
  }
};

/**
 * Format invoice number for display
 * PUEX-25-000000001 => PUEX/25/000000001
 */
export const formatInvoiceNumber = (invoiceNumber) => {
  if (!invoiceNumber) return '';
  return invoiceNumber.replace(/-/g, '/');
};

/**
 * Format date for invoice display
 */
export const formatInvoiceDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Format currency for invoice
 */
export const formatCurrency = (amount) => {
  if (!amount) return '₹0.00';
  return `₹${parseFloat(amount).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * Get invoice type display name
 */
export const getInvoiceTypeDisplayName = (invoiceType) => {
  const types = {
    'PUEX': 'Photo Upload Extension',
    'PUUP': 'Photo Upload Upgrade',
  };
  return types[invoiceType] || invoiceType;
};

/**
 * Get purchase type display name
 */
export const getPurchaseTypeDisplayName = (purchaseType) => {
  const types = {
    'extension': 'Extension',
    'upgrade': 'Upgrade',
  };
  return types[purchaseType] || purchaseType;
};
